import { useMemo } from 'react';
import { type ColumnDef } from '@tanstack/react-table';
import { X } from 'lucide-react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { DataView } from '@/components/data/DataView';
import { DrillLink } from '@/components/navigation/DrillLink';
import { Skeleton } from '@/components/ui/Skeleton';
import { ErrorState } from '@/components/ui/ErrorState';
import { useClientPagination } from '@/hooks/useClientPagination';
import {
  useProductDrilldown,
  type ProductDrilldownCustomerRow,
  type ProductDrilldownFilters,
  type ProductDrilldownOrderRow,
} from '@/api/bi';
import { fmt } from '@/lib/format';
import { cn } from '@/lib/cn';

interface ProductDrillTablesProps {
  returnPath: string;
  filters: ProductDrilldownFilters;
  enabled?: boolean;
}

/** Orders + customers tables for a scoped product drill-down query. */
export function ProductDrillTables({ returnPath, filters, enabled = true }: ProductDrillTablesProps) {
  const drill = useProductDrilldown({ ...filters, limit: 50, enabled });

  const orders = drill.data?.orders ?? [];
  const customers = drill.data?.customers ?? [];
  const ordersPagination = useClientPagination(10, orders.length);
  const customersPagination = useClientPagination(10, customers.length);

  const orderColumns = useMemo<ColumnDef<ProductDrilldownOrderRow, unknown>[]>(
    () => [
      {
        id: 'order',
        header: 'Order',
        cell: ({ row }) => (
          <DrillLink to={`/orders/${row.original.order_id}`} from={returnPath}>
            #{row.original.order_id}
          </DrillLink>
        ),
      },
      {
        id: 'customer',
        header: 'Customer',
        cell: ({ row }) =>
          row.original.customer_id ? (
            <DrillLink to={`/customers/${row.original.customer_id}`} from={returnPath}>
              {row.original.customer_name ?? `#${row.original.customer_id}`}
            </DrillLink>
          ) : (
            <span className="text-xs text-muted-foreground">—</span>
          ),
      },
      {
        id: 'store',
        header: 'Store',
        cell: ({ row }) => (
          <span className="truncate text-xs">{row.original.store_name ?? '—'}</span>
        ),
      },
      {
        id: 'gmv',
        header: () => <div className="text-right">GMV</div>,
        cell: ({ row }) => (
          <div className="kpi-num text-right text-xs">{fmt.npr(row.original.gmv)}</div>
        ),
      },
      {
        id: 'units',
        header: () => <div className="text-right">Units</div>,
        cell: ({ row }) => (
          <div className="kpi-num text-right text-xs">{fmt.number(row.original.units)}</div>
        ),
      },
      {
        id: 'completed',
        header: () => <div className="text-right">Completed</div>,
        cell: ({ row }) => (
          <div className="text-right text-xs text-muted-foreground">
            {row.original.completed_timestamp ? fmt.date(row.original.completed_timestamp, 'd MMM HH:mm') : '—'}
          </div>
        ),
      },
    ],
    [returnPath],
  );

  const customerColumns = useMemo<ColumnDef<ProductDrilldownCustomerRow, unknown>[]>(
    () => [
      {
        id: 'name',
        header: 'Customer',
        cell: ({ row }) => (
          <DrillLink to={`/customers/${row.original.customer_id}`} from={returnPath}>
            {row.original.name ?? `#${row.original.customer_id}`}
          </DrillLink>
        ),
      },
      {
        id: 'orders',
        header: () => <div className="text-right">Orders</div>,
        cell: ({ row }) => (
          <div className="kpi-num text-right text-xs">{fmt.number(row.original.orders)}</div>
        ),
      },
      {
        id: 'gmv',
        header: () => <div className="text-right">GMV</div>,
        cell: ({ row }) => (
          <div className="kpi-num text-right text-xs">{fmt.npr(row.original.gmv)}</div>
        ),
      },
      {
        id: 'units',
        header: () => <div className="text-right">Units</div>,
        cell: ({ row }) => (
          <div className="kpi-num text-right text-xs">{fmt.number(row.original.units)}</div>
        ),
      },
      {
        id: 'last',
        header: () => <div className="text-right">Last order</div>,
        cell: ({ row }) => (
          <div className="text-right text-xs text-muted-foreground">
            {row.original.last_order ? fmt.date(row.original.last_order, 'd MMM') : '—'}
          </div>
        ),
      },
    ],
    [returnPath],
  );

  if (drill.isLoading) return <Skeleton className="h-48 w-full" />;
  if (drill.isError) return <ErrorState onRetry={() => void drill.refetch()} />;

  const summary = drill.data?.summary;

  return (
    <div className="space-y-4">
      {summary ? (
        <div className="grid gap-3 sm:grid-cols-4">
          <Stat label="Orders" value={fmt.number(summary.orders)} />
          <Stat label="GMV" value={fmt.npr(summary.gmv)} />
          <Stat label="Units" value={fmt.number(summary.units)} />
          <Stat label="Customers" value={fmt.number(summary.unique_customers)} />
        </div>
      ) : null}
      <div className="grid gap-4 xl:grid-cols-2">
        <DataView
          title="Recent orders"
          data={orders}
          columns={orderColumns}
          clientPagination
          pagination={{ ...ordersPagination, total: orders.length }}
          density="compact"
          emptyTitle="No orders in scope"
        />
        <DataView
          title="Top customers"
          data={customers}
          columns={customerColumns}
          clientPagination
          pagination={{ ...customersPagination, total: customers.length }}
          density="compact"
          emptyTitle="No customers in scope"
        />
      </div>
    </div>
  );
}

interface ProductDrillDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  returnPath: string;
  filters: ProductDrilldownFilters;
}

/** Slide-over drawer for taxonomy-level order/customer drill-down. */
export function ProductDrillDrawer({
  open,
  onOpenChange,
  title,
  returnPath,
  filters,
}: ProductDrillDrawerProps) {
  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-[1000] bg-black/40 backdrop-blur-sm data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />
        <DialogPrimitive.Content
          className={cn(
            'fixed inset-y-0 right-0 z-[1010] flex w-full max-w-3xl flex-col border-l border-border bg-surface shadow-kpi',
            'data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right duration-240',
          )}
        >
          <div className="flex items-center justify-between border-b border-border px-4 py-3">
            <DialogPrimitive.Title className="text-sm font-semibold">{title}</DialogPrimitive.Title>
            <DialogPrimitive.Close
              className="press rounded-md p-1 text-muted-foreground hover:text-foreground focus-ring"
              aria-label="Close"
            >
              <X className="h-4 w-4" />
            </DialogPrimitive.Close>
          </div>
          <div className="flex-1 overflow-y-auto p-4">
            <ProductDrillTables returnPath={returnPath} filters={filters} enabled={open} />
          </div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-md border border-border bg-surface px-3 py-2">
      <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">{label}</p>
      <p className="kpi-num mt-0.5 text-sm font-semibold">{value}</p>
    </div>
  );
}
